import { logger } from '../services/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, HelpCircle, Plus, Trash2, Image as ImageIcon, Zap } from 'lucide-react';
import { MarketCategory, DbMarketType } from '../types';
import { supabase } from '../lib/db';
import { preserveLocalDateTime } from '../utils/dateUtils';
import { validateMarketCardImage } from '../utils/imageUtils';
import { Category as CategoryType, fetchMarketCategories } from '../services/categoryService';
import { useAuth } from '../lib/auth';
import { MarketStandardsAgreementModal } from '../components/MarketStandardsAgreementModal.tsx';
import { submitTradingMarket } from '../lib/api.ts';

interface CreateFormOption {
  name: string;
  trading_end_time?: string | null;
}

export function CreateMarket() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminUser =
    user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [category, setCategory] = useState<MarketCategory>('Sports');
  const [marketType, setMarketType] = useState<DbMarketType>('fixed');
  const [options, setOptions] = useState<CreateFormOption[]>([{ name: '', trading_end_time: null }, { name: '', trading_end_time: null }]);
  const [resolutionDate, setResolutionDate] = useState('');
  const [resolutionTime, setResolutionTime] = useState('');
  /** No fixed resolution date yet (TBA until set in admin/editor). */
  const [resolutionOpenEnded, setResolutionOpenEnded] = useState(false);
  const [rules, setRules] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lmsrTier, setLmsrTier] = useState<'standard' | 'enhanced' | 'premium'>('standard');

  const LMSR_TIERS: Record<'standard' | 'enhanced' | 'premium', { b: number; label: string; description: string }> = {
    standard: { b: 500, label: 'Standard', description: 'Entry depth — wider price movement' },
    enhanced: { b: 1500, label: 'Enhanced', description: 'Balanced depth and stability' },
    premium: { b: 5000, label: 'Premium', description: 'Deepest book — prices move more gradually' },
  };

  const lmsrDeposit = (): number => {
    const b = LMSR_TIERS[lmsrTier].b;
    const n = Math.max(options.length, 2);
    return parseFloat((b * Math.log(n)).toFixed(2));
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasAgreedToStandards, setHasAgreedToStandards] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [currentStandardsVersion, setCurrentStandardsVersion] = useState<number | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchMarketCategories();
      setCategories(fetchedCategories);
      if (fetchedCategories.length > 0) {
        setCategory(fetchedCategories[0].name as MarketCategory);
      }
    };
    loadCategories();
  }, []);

  // Check if user has agreed to market standards (skip for admins)
  useEffect(() => {
    const checkAgreement = async () => {
      if (!user) {
        setHasAgreedToStandards(false);
        return;
      }

      // Skip for admins
      const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
      if (isAdmin) {
        setHasAgreedToStandards(true);
        return;
      }

      try {
        // First, get the current version of market standards
        const { data: standardsData, error: standardsError } = await supabase
          .rpc('get_active_site_content', { p_content_type: 'market_standards' });

        if (standardsError) {
          logger.error('Error fetching market standards:', standardsError);
          setHasAgreedToStandards(false);
          return;
        }

        if (!standardsData || standardsData.length === 0) {
          // No standards exist yet, allow creation
          setHasAgreedToStandards(true);
          setCurrentStandardsVersion(null);
          return;
        }

        const currentVersion = standardsData[0].version;
        setCurrentStandardsVersion(currentVersion);

        // Check if user has agreed to this specific version
        const { data: agreementData, error: agreementError } = await supabase
          .from('user_market_standards_agreement')
          .select('version, agreed_at')
          .eq('user_id', user.id)
          .eq('version', currentVersion)
          .maybeSingle(); // Use maybeSingle instead of single to avoid errors when no record exists

        if (agreementError) {
          // If it's a 403, it's a permissions issue - log but don't block
          if (agreementError.code === 'PGRST301' || agreementError.message?.includes('403')) {
            logger.error('Permission error checking agreement - RLS policy issue:', agreementError);
            // Allow creation but log the issue for admin review
            setHasAgreedToStandards(true);
            return;
          }
          logger.error('Error checking agreement:', agreementError);
          setHasAgreedToStandards(false);
          return;
        }

        // If data exists AND version matches current version, user has agreed
        const hasAgreed = !!agreementData && agreementData.version === currentVersion;
        setHasAgreedToStandards(hasAgreed);
        
        // Show modal ONLY if user hasn't agreed to the current version
        if (!hasAgreed) {
          setShowAgreementModal(true);
        } else {
          setShowAgreementModal(false);
        }
      } catch (err) {
        logger.error('Error checking agreement:', err);
        setHasAgreedToStandards(false);
        setShowAgreementModal(true);
      }
    };

    checkAgreement();
  }, [user]);

  // Handle agreement
  const handleAgree = async () => {
    if (!user) return;

    if (currentStandardsVersion === null) {
      logger.error('Unable to determine market standards version');
      return;
    }

    try {
      // Use upsert to handle case where agreement already exists
      // For composite primary keys, we need to specify both columns
      const { error } = await supabase
        .from('user_market_standards_agreement')
        .upsert({
          user_id: user.id,
          version: currentStandardsVersion,
          agreed_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,version',
          ignoreDuplicates: false // Update if exists
        });

      if (error) {
        // If it's a 403, it's a permissions issue
        if (error.code === 'PGRST301' || error.message?.includes('403')) {
          logger.error('Permission error saving agreement - RLS policy issue:', error);
          return;
        }
        throw error;
      }

      setHasAgreedToStandards(true);
      setShowAgreementModal(false);
      
      // Re-check agreement to ensure state is correct
      const { data: verifyData } = await supabase
        .from('user_market_standards_agreement')
        .select('version')
        .eq('user_id', user.id)
        .eq('version', currentStandardsVersion)
        .maybeSingle();
      
      if (verifyData) {
        setHasAgreedToStandards(true);
      }
    } catch (err) {
      logger.error('Error saving agreement:', err);
    }
  };

  const handleOptionChange = (index: number, field: 'name' | 'trading_end_time', value: string) => {
    const newOptions = [...options];
    const currentOption = { ...newOptions[index] };

    if (field === 'name') {
      currentOption.name = value;
    } else if (field === 'trading_end_time') {
      currentOption.trading_end_time = preserveLocalDateTime(value);
    }

    newOptions[index] = currentOption;
    setOptions(newOptions);

    const newErrors = { ...errors };
    delete newErrors[`option_name_${index}`];
    delete newErrors[`option_time_${index}`];
    setErrors(newErrors);
  };

  const addOption = () => {
    setOptions([...options, { name: '', trading_end_time: null }]);
  };

  const removeOption = (index: number) => {
    if (options.length <= 2) return;
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
    const newErrors = { ...errors };
    delete newErrors[`option_name_${index}`];
    delete newErrors[`option_time_${index}`];
    setErrors(newErrors);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Please upload an image file' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 5MB' }));
      return;
    }

    // Validate image dimensions
    const validation = await validateMarketCardImage(file);
    if (!validation.isValid) {
      // Show warnings but don't block upload
      validation.warnings.forEach(warning => {
        console.warn(warning);
      });
      if (validation.recommendations.length > 0) {
        console.info(`Tip: ${validation.recommendations[0]}`);
      }
    }

    setCoverImage(file);
    setImagePreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, image: '' }));
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `market-covers/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('market-images')
      .upload(filePath, file);

    if (uploadError) {
      throw new Error('Failed to upload image');
    }

    const { data: { publicUrl } } = supabase.storage
      .from('market-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const now = new Date();

    if (!title.trim()) newErrors.title = 'Market title is required';
    if (!category) newErrors.category = 'Category is required';

    let resolutionDateTime: Date | null = null;
    if (!resolutionOpenEnded) {
      if (!resolutionDate) {
        newErrors.resolutionDate = 'Pick a resolution date, or choose “no date yet”.';
      } else {
        resolutionDateTime = new Date(`${resolutionDate}T${resolutionTime || '00:00'}`);
        if (resolutionDateTime <= now) {
          newErrors.resolutionDate = 'Resolution date must be in the future';
        }
      }
    }

    options.forEach((option, index) => {
      if (!option.name.trim()) {
        newErrors[`option_name_${index}`] = `Option ${index + 1} name is required`;
      }
    });

    if (options.some(opt => !opt.name.trim())) {
      newErrors.options = 'All options must have a name';
    }

    if (!rules.trim()) newErrors.rules = 'Market rules are required';
    if (!coverImage) newErrors.image = 'Cover image is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user has agreed to standards (skip for admins)
    if (!isAdminUser && !hasAgreedToStandards) {
      setShowAgreementModal(true);
      setErrors(prev => ({ ...prev, submit: 'You must agree to the market standards before creating a market' }));
      return;
    }
    
    if (!validateForm() || !coverImage) return;

    setUploading(true);
    try {
      const imageUrl = await uploadImage(coverImage);

      const finalResolutionTime = resolutionOpenEnded
        ? null
        : resolutionDate
          ? preserveLocalDateTime(`${resolutionDate}T${resolutionTime || '00:00'}`)
          : null;

      const optionsPayload = options.map(opt => ({
        name: opt.name.trim(),
        trading_end_time: null as string | null,
      }));

      if (isAdminUser) {
        const rpcPayload = {
          p_title: title.trim(),
          p_description: rules.trim(),
          p_category: category,
          p_market_type: marketType,
          p_is_private: false,
          p_is_dynamic: true,
          p_fixed_fee: null,
          p_trading_end_time: null,
          p_resolution_time: finalResolutionTime,
          p_initial_options: optionsPayload,
          p_status: 'active',
          p_image_url: imageUrl,
          p_featured_image_url: null,
          p_is_featured: false,
          p_engine_type: 'lmsr',
          p_lmsr_b: LMSR_TIERS[lmsrTier].b,
        };

        const { data: newMarketId, error: rpcError } = await supabase.rpc('create_market_v2', rpcPayload);
        if (rpcError) throw rpcError;
        const createdId = Array.isArray(newMarketId) ? newMarketId[0] : newMarketId;
        if (!createdId) throw new Error('Failed to create market - no ID returned');
        navigate('/admin/markets');
      } else {
        const data = await submitTradingMarket({
          title: title.trim(),
          description: rules.trim(),
          category,
          market_type: marketType,
          is_private: false,
          is_dynamic: true,
          resolution_time: finalResolutionTime,
          trading_end_time: null,
          initial_options: optionsPayload,
          image_url: imageUrl,
          featured_image_url: null,
          is_featured: false,
        });
        if (!data?.market_id) throw new Error('Submission did not return a market id');
        navigate('/account/creator');
      }

    } catch (error: unknown) {
      logger.error('Error creating market:', error);
      let errorMessage = 'Failed to create market';
      if (error instanceof Error) {
          errorMessage = error.message;
      }
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    } finally {
      setUploading(false);
    }
  };

  const getLocalISOString = (isoString: string | null | undefined): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="pt-8 pb-24 md:py-8">
      <MarketStandardsAgreementModal
        isOpen={showAgreementModal}
        onAgree={handleAgree}
      />
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Create New Market</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Market Title
            </label>
            <div className="relative">
              <HelpCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Who will win the next presidential election?"
                className={`pl-10 w-full rounded-md border ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                } px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
              />
            </div>
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as MarketCategory)}
                className={`pl-4 pr-10 w-full rounded-md border ${
                  errors.category ? 'border-red-500' : 'border-gray-300'
                } py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                disabled={categories.length === 0}
              >
                <option value="" disabled>-- Select Category --</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cover Image
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Cover preview"
                      className="mx-auto h-32 w-auto rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCoverImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                )}
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="cover-image"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-wayja-teal hover:text-wayja-teal-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-wayja-teal"
                  >
                    <span>Upload a file</span>
                    <input
                      id="cover-image"
                      name="cover-image"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleImageChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 5MB
                </p>
              </div>
            </div>
            {errors.image && <p className="mt-1 text-sm text-red-500">{errors.image}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Market Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="marketType"
                  value="fixed"
                  checked={marketType === 'fixed'}
                  onChange={(e) => setMarketType(e.target.value as DbMarketType)}
                  className="form-radio h-4 w-4 text-wayja-teal focus:ring-wayja-teal"
                />
                <span className="text-sm">Standard Market</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="marketType"
                  value="knockout"
                  checked={marketType === 'knockout'}
                  onChange={(e) => setMarketType(e.target.value as DbMarketType)}
                  className="form-radio h-4 w-4 text-wayja-teal focus:ring-wayja-teal"
                />
                <span className="text-sm">Knockout Market</span>
              </label>
            </div>
          </div>

          {isAdminUser && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
              <label
                className="block text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2"
                title="Higher b means deeper liquidity: prices move more slowly as people trade."
              >
                <Zap className="w-4 h-4 text-wayja-teal" />
                Liquidity depth (b = {LMSR_TIERS[lmsrTier].b})
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(Object.keys(LMSR_TIERS) as Array<'standard' | 'enhanced' | 'premium'>).map((tier) => (
                  <label
                    key={tier}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      lmsrTier === tier
                        ? 'border-wayja-teal bg-wayja-teal/5 ring-1 ring-wayja-teal'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="lmsrTier"
                      value={tier}
                      checked={lmsrTier === tier}
                      onChange={() => setLmsrTier(tier)}
                      className="sr-only"
                    />
                    <p className="text-sm font-semibold text-gray-900">{LMSR_TIERS[tier].label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{LMSR_TIERS[tier].description}</p>
                  </label>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                <p className="font-medium text-amber-800">Liquidity depth (Wayja-funded)</p>
                <p className="text-amber-700 mt-1">
                  At this depth, backing of roughly R{lmsrDeposit().toFixed(2)} applies for{' '}
                  {Math.max(options.length, 2)} outcomes (b × ln(N)). Wayja provides this liquidity; it is not deducted
                  from your wallet when you create the market.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Market Options
            </label>
            <div className="space-y-3">
              {options.map((option, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 items-start">
                  <div className="flex-1 w-full">
                    <input
                      type="text"
                      value={option.name}
                      onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                      placeholder={`Option ${index + 1} Name`}
                      className={`w-full rounded-md border ${
                        errors[`option_name_${index}`] ? 'border-red-500' : 'border-gray-300'
                      } px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                    />
                    {errors[`option_name_${index}`] && <p className="mt-1 text-xs text-red-500">{errors[`option_name_${index}`]}</p>}
                  </div>

                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 text-red-500 hover:text-red-700 self-center sm:self-auto"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.options && <p className="mt-1 text-sm text-red-500">{errors.options}</p>}
            <button
              type="button"
              onClick={addOption}
              className="mt-2 flex items-center gap-2 text-wayja-teal hover:text-wayja-teal-dark"
            >
              <Plus className="w-4 h-4" />
              Add Option
            </button>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-gray-200 bg-gray-50/80 p-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-wayja-teal focus:ring-wayja-teal"
                checked={resolutionOpenEnded}
                onChange={(e) => {
                  const on = e.target.checked;
                  setResolutionOpenEnded(on);
                  if (on) {
                    setResolutionDate('');
                    setResolutionTime('');
                  }
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.resolutionDate;
                    return next;
                  });
                }}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">Resolution time not set yet (TBA)</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Use this for open-ended topics (event might not have an announced deadline). Markets show &quot;TBA&quot;
                  until an admin schedules a resolution time later.
                </span>
              </span>
            </label>

          <div className={`grid grid-cols-2 gap-4 ${resolutionOpenEnded ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Market Resolution Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="date"
                  value={resolutionDate}
                  onChange={(e) => setResolutionDate(e.target.value)}
                  className={`pl-10 w-full rounded-md border ${
                    errors.resolutionDate ? 'border-red-500' : 'border-gray-300'
                  } px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Market Resolution Time
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="time"
                  value={resolutionTime}
                  onChange={(e) => setResolutionTime(e.target.value)}
                  className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                />
              </div>
            </div>
            {errors.resolutionDate && (
              <p className="col-span-2 -mt-4 text-sm text-red-500">{errors.resolutionDate}</p>
            )}
          </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Market Rules and Terms
            </label>
            <textarea
              value={rules}
              onChange={(e) => setRules(e.target.value)}
              placeholder="Enter the rules and terms for market resolution..."
              rows={4}
              className={`w-full rounded-md border ${
                errors.rules ? 'border-red-500' : 'border-gray-300'
              } px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent`}
            />
            {errors.rules && <p className="mt-1 text-sm text-red-500">{errors.rules}</p>}
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/markets')}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 bg-wayja-teal text-white px-6 py-2 rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
            >
              {uploading
                ? 'Working...'
                : isAdminUser
                  ? 'Create Market'
                  : 'Submit for approval'}
            </button>
          </div>

          {errors.submit && (
            <p className="mt-2 text-center text-sm text-red-500">{errors.submit}</p>
          )}
        </form>
      </div>
    </div>
  );
}